import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiEdit2, 
  FiTrash2, 
  FiPlus, 
  FiCalendar,
  FiSearch,
  FiChevronUp,
  FiChevronDown,
  
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ConfirmationModal from '../components/confirmation';
import './expenses.css';

const ExpensesList = () => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1
  });
  const [dateSort, setDateSort] = useState('desc');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState({
    month_total: 0,
    year_total: 0,
    category_totals: []
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const { page, limit } = pagination;

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        sort: dateSort
      };

      if (dateFilter.startDate) params.start_date = dateFilter.startDate;
      if (dateFilter.endDate) params.end_date = dateFilter.endDate;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (categoryFilter !== 'all') params.category = categoryFilter;

      const response = await axios.get('/expenses', {
        params
      });
      const responseData = response.data;
      const expenseRows = responseData.data || responseData;

      setExpenses(expenseRows);
      setSummary(responseData.summary || {
        month_total: 0,
        year_total: 0,
        category_totals: []
      });
      setPagination((prev) => ({
        ...prev,
        ...(responseData.pagination || {}),
        totalItems: responseData.pagination?.totalItems ?? expenseRows.length,
        totalPages: responseData.pagination?.totalPages ?? Math.ceil(expenseRows.length / limit)
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch expenses');
      console.error('Error fetching expenses:', err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, dateSort, dateFilter, searchTerm, categoryFilter]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get('/expenses/categories');
        setCategories(response.data);
      } catch (err) {
        console.error('Error fetching expense categories:', err);
      }
    };

    fetchCategories();
  }, []);

  const handleDeleteClick = (id) => {
    setExpenseToDelete(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/expenses/${expenseToDelete}`);
      fetchExpenses(); // Refresh the list after deletion
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete expense');
    } finally {
      setShowDeleteModal(false);
      setExpenseToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setExpenseToDelete(null);
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const toggleDateSort = () => {
    setDateSort(dateSort === 'desc' ? 'asc' : 'desc');
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilter({
      ...dateFilter,
      [name]: value
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearDateFilters = () => {
    setDateFilter({
      startDate: '',
      endDate: ''
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  if (loading) {
    return <div className="loading">Loading expenses data...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="expenses-container">
      <div className="expenses-header">
        <h1 className="expenses-title">Expenses Records</h1>
        <button 
          className="add-expense-btn"
          onClick={() => navigate('/expenses/add')}
        >
          <FiPlus />
          Add Expense
        </button>
      </div>

      <div className="expense-summary-grid">
        <div className="expense-summary-card total">
          <span>This Month</span>
          <strong>Rs {formatCurrency(summary.month_total)}</strong>
        </div>
        <div className="expense-summary-card total">
          <span>This Year</span>
          <strong>Rs {formatCurrency(summary.year_total)}</strong>
        </div>
        {summary.category_totals
          .filter((item) => Number(item.month_amount) > 0 || Number(item.year_amount) > 0)
          .slice(0, 6)
          .map((item) => (
            <div className="expense-summary-card" key={item.category}>
              <span>{item.category}</span>
              <strong>Rs {formatCurrency(item.month_amount)}</strong>
              <small>this month / Rs {formatCurrency(item.year_amount)} this year</small>
            </div>
          ))}
      </div>

      {/* Date Filter Controls */}
      <div className="filters-container">
        <div className="date-filters">
          <div className="filter-group search-filter">
            <label htmlFor="expenseSearch">Search:</label>
            <div className="search-input-wrap">
              <FiSearch className="search-icon" />
              <input
                type="search"
                id="expenseSearch"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by expense name"
              />
            </div>
          </div>
          <div className="filter-group">
            <label htmlFor="categoryFilter">Category:</label>
            <select
              id="categoryFilter"
              value={categoryFilter}
              onChange={handleCategoryFilterChange}
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="startDate">From:</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={dateFilter.startDate}
              onChange={handleDateFilterChange}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="endDate">To:</label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={dateFilter.endDate}
              onChange={handleDateFilterChange}
            />
          </div>
          <button 
            className="clear-filters-btn"
            onClick={clearDateFilters}
          >
            Clear Filters
          </button>
        </div>
      </div>

      <table className="expenses-table">
        <thead>
          <tr>
            <th>Expense Name</th>
            <th>Category</th>
            <th>Amount (Rs)</th>
            <th>
              <div className="sortable-header" onClick={toggleDateSort}>
                <FiCalendar />
                Payment Date
                {dateSort === 'desc' ? (
                  <FiChevronDown className="sort-icon" />
                ) : (
                  <FiChevronUp className="sort-icon" />
                )}
              </div>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {expenses.map((expense) => (
            <tr key={expense.expense_id}>
              <td>{expense.name}</td>
              <td>
                <span className="category-badge">{expense.category || 'Other'}</span>
              </td>
              <td>{formatCurrency(expense.amount)}</td>
              <td>
                {new Date(expense.paid_date).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </td>
              <td>
                <div className="action-buttons">
                  <button
                    onClick={() => navigate(`/expenses/edit/${expense.expense_id}`)}
                    className="edit-btn"
                    title="Edit"
                  >
                    <FiEdit2 size={16} />
                  </button>
                  <button 
                    onClick={() => handleDeleteClick(expense.expense_id)}
                    className="delete-btn"
                    title="Delete"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ConfirmationModal
        show={showDeleteModal}
        title="Delete Expense"
        message="Are you sure you want to delete this expense? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {expenses.length === 0 && (
        <div className="empty-state">
          <p>No expenses records found matching your criteria.</p>
        </div>
      )}

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(1)}
            disabled={pagination.page === 1}
          >
            First
          </button>
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            Previous
          </button>
          
          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
            const pageNum = pagination.page <= 3 
              ? i + 1 
              : Math.min(pagination.totalPages - 4, pagination.page - 2) + i;
            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={pagination.page === pageNum ? 'active' : ''}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
          >
            Next
          </button>
          <button
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={pagination.page === pagination.totalPages}
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
};

export default ExpensesList;
